import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Plus, RefreshCw, Loader2, Pencil, Info, Trash2, Users, X } from 'lucide-react';
import PageHeader, { AccountSelector } from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import api, { apiError } from '../../lib/api';
import ConvAreaChart from '../../components/ConvAreaChart';
import { getCached, setCached } from '../../lib/pageCache';
import { chargerComptes } from '../../lib/comptesPartages';

const SOURCES = ['Instagram', 'Snapchat', 'TikTok', 'X (Twitter)', 'Reddit', 'YouTube', 'Facebook', 'Threads', 'OnlyFans', 'Share for Share', 'Autre'];
// #146 (16/09) : dernier modèle + canal ouverts, pour lancer la liste des liens DÈS l'ouverture de la page, en
// parallèle du chargement des comptes et des canaux (avant : comptes → canaux → liens, l'un après l'autre).
const DERNIER_CANAL = 'chathub_tracking_dernier_canal';
const lireDernier = () => { try { return JSON.parse(localStorage.getItem(DERNIER_CANAL) || 'null') || {}; } catch { return {}; } };
const PERIODES_CLES = ['yesterday', 'today', '7d', '30d', 'month'];
const sansStats = ({ id, title, source, link }) => ({ id, title, source, link });
const EN_ATTENTE = '…';
// Un lien créé depuis la page reste affiché même si une réponse calculée juste avant sa création arrive après.
const avecCrees = (crees, canal, liste) => [
  ...crees.filter(c => c.canal === canal && !liste.some(l => l.id === c.ligne.id || l.link === c.ligne.link)).map(c => c.ligne),
  ...liste,
];

export default function TrackingLinks() {
  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState(() => lireDernier().account || '');
  const [channels, setChannels] = useState([]);
  const [channel, setChannel] = useState(() => lireDernier().channel || '');
  // Compte auquel appartient `channel` : tant que les canaux du compte choisi ne sont pas arrivés, on ne demande
  // pas les liens d'un couple compte/canal faux (avant : un appel Telegram inutile à chaque changement de modèle).
  const [channelOf, setChannelOf] = useState(() => (lireDernier().channel ? lireDernier().account || '' : ''));
  const [links, setLinks] = useState([]);
  const [nouveauId, setNouveauId] = useState('');     // lien tout juste créé : affiché en tête, surligné
  const [title, setTitle] = useState('');
  const [source, setSource] = useState('Instagram');
  const [filterSource, setFilterSource] = useState('');
  const [sortKey, setSortKey] = useState('joins');   // default: most joins first
  const [sortDir, setSortDir] = useState('desc');
  const [detail, setDetail] = useState(null);        // { lien, fans, ... }
  const [detailLoading, setDetailLoading] = useState('');
  // QUI se cache derrière les chiffres d'un lien (la liste des entrants vit chez
  // Telegram, pas en base — d'où l'appel dédié).
  const voirFans = async (l, periode) => {
    setDetailLoading(l.id);
    try {
      const { data } = await api.get('/tracking/link-fans', {
        params: { account_id: account, channel_id: channel, link_id: l.id, period: periode },
        timeout: 60000,
      });
      setDetail(data);
    } catch (e) { toast.error(apiError(e, 'Échec du chargement du détail')); }
    setDetailLoading('');
  };

  // RAMENÉS = ce que la page a apporté, jamais « ceux qui restent ».
  // Sur « Tout », `joins_period` retombe sur le compteur Telegram live (= les
  // présents) : on prend alors le cumul du registre, qui ne redescend jamais.
  // REJOINTS = combien de personnes ont rejoint le Telegram par ce lien. Le back
  // renvoie le compte de la période, et `joins_depuis` quand l'historique des
  // compteurs ne remonte pas jusqu'au début de la période (on dit alors depuis
  // quelle date on compte, plutôt que de faire passer un total pour un mois).
  const rejoints = (l) => Number(l.joins_period ?? l.joins_total ?? l.joins) || 0;
  const depuis = (l) => l.joins_depuis || null;
  const jourFr = (d) => (d && d !== 'toujours'
    ? d.split('-').reverse().slice(0, 2).join('/') : null);

  const toggleSort = (key) => {
    if (sortKey === key) { setSortDir(d => (d === 'desc' ? 'asc' : 'desc')); }
    else { setSortKey(key); setSortDir('desc'); }
  };
  const [deletingId, setDeletingId] = useState('');
  const [loadingCh, setLoadingCh] = useState(false);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [creating, setCreating] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [resolveQuery, setResolveQuery] = useState('');
  const [resolving, setResolving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [period, setPeriod] = useState('today'); // défaut : les stats du JOUR quand on arrive
  const [timeseries, setTimeseries] = useState([]);
  const [funnel, setFunnel] = useState([]); // funnel marketing PAR MODÈLE

  useEffect(() => {
    api.get('/tracking/funnel', { params: { period } })
      .then(({ data }) => setFunnel(data.funnel || [])).catch(() => setFunnel([]));
  }, [period]);

  const PERIODS = [['yesterday', 'Hier'], ['today', "Aujourd'hui"], ['7d', '7j'], ['30d', '30j'], ['month', 'Mois']];

  useEffect(() => {
    // Liste partagée avec le menu (un seul /accounts) ; le dernier modèle ouvert reste choisi s'il est encore accessible.
    chargerComptes().then((data) => {
      setAccounts(data);
      setAccount(prev => (data.some(a => a.id === prev) ? prev : (data[0]?.id || '')));
    }).catch(() => {});
  }, []);

  // Channels load INSTANTLY from the server cache; a slow Telegram scan runs in the
  // background (data.scanning) and we re-poll until it lands. force=true re-scans.
  const loadChannels = useCallback((force = false) => {
    if (!account) { setChannels([]); setChannel(''); return; }
    setLoadingCh(true);
    api.get('/tracking/channels', { params: { account_id: account, refresh: force || undefined } })
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : (data.channels || []);
        setChannels(list);
        setChannel(prev => (list.some(x => x.id === prev) ? prev : (list[0]?.id || '')));
        setChannelOf(account);
        setScanning(!Array.isArray(data) && !!data.scanning);
      })
      .catch(e => toast.error(apiError(e, 'Chargement des canaux échoué')))
      .finally(() => setLoadingCh(false));
  }, [account]);

  useEffect(() => {
    if (!account || !channel || channelOf !== account) return;
    try { localStorage.setItem(DERNIER_CANAL, JSON.stringify({ account, channel })); } catch { /* stockage indisponible */ }
  }, [account, channel, channelOf]);

  useEffect(() => { loadChannels(false); }, [loadChannels]);
  // While a background scan runs, re-poll so freshly-found channels appear on their own.
  useEffect(() => {
    if (!scanning) return undefined;
    const id = setTimeout(() => loadChannels(false), 12000);
    return () => clearTimeout(id);
  }, [scanning, loadChannels]);

  const demandeLiens = useRef(0);     // une réponse d'un ancien canal / d'une ancienne période est ignorée
  const canalAffiche = useRef('');    // compte:canal des lignes affichées
  const creesIci = useRef([]);        // liens créés depuis cette page : [{ canal, ligne }]
  const oublierCanal = (acc, ch) => PERIODES_CLES.forEach(p => setCached(`tlinks:${acc}:${ch}:${p}`, null));

  const loadLinks = useCallback((fresh = false) => {
    const n = ++demandeLiens.current;
    if (!account || !channel) { setLinks([]); setTimeseries([]); setLoadingLinks(false); return; }
    if (channelOf !== account) { setLinks([]); setTimeseries([]); setLoadingLinks(true); return; } // canaux du modèle en route
    const canal = `${account}:${channel}`;
    // Local-first : réaffiche la dernière vue (cache) tout de suite, puis rafraîchit en fond.
    const key = `tlinks:${account}:${channel}:${period}`;
    const cached = getCached(key);
    if (cached) { setLinks(cached.links || []); setTimeseries(cached.timeseries || []); canalAffiche.current = canal; }
    else {
      // Même canal, autre période : on garde les noms, les chiffres passent en attente. Autre canal : rien.
      setLinks(prev => (canalAffiche.current === canal ? prev.map(sansStats) : []));
      setTimeseries([]);
    }
    setLoadingLinks(true);
    let complet = false;
    if (!cached) {
      // #146 : LISTE D'ABORD (base seule, instantané), chiffres ensuite (Telegram) — les deux partent en même temps.
      api.get('/tracking/links', { params: { account_id: account, channel_id: channel, period, light: 1 } })
        .then(({ data }) => {
          if (n !== demandeLiens.current || complet) return;
          setLinks(avecCrees(creesIci.current, canal, (Array.isArray(data) ? data : (data.links || [])).map(sansStats)));
          canalAffiche.current = canal;
        })
        .catch(() => {});
    }
    api.get('/tracking/links', { params: { account_id: account, channel_id: channel, period, fresh: fresh || undefined } })
      .then(({ data }) => {
        if (n !== demandeLiens.current) return;
        complet = true;
        // Nouveau format { links, timeseries } ; rétro-compatible si l'API renvoie un tableau.
        const links = avecCrees(creesIci.current, canal, Array.isArray(data) ? data : (data.links || []));
        const timeseries = Array.isArray(data) ? [] : (data.timeseries || []);
        setLinks(links);
        setTimeseries(timeseries);
        canalAffiche.current = canal;
        setCached(key, { links, timeseries });
      })
      .catch(e => {
        if (n === demandeLiens.current && !cached && e?.response?.status !== 403) toast.error(apiError(e, 'Chargement des liens échoué'));
      })
      .finally(() => { if (n === demandeLiens.current) setLoadingLinks(false); });
  }, [account, channel, channelOf, period]);

  useEffect(() => { loadLinks(); }, [loadLinks]);

  const importLinks = async () => {
    try {
      const { data } = await api.post('/tracking/links/import', null, { params: { account_id: account, channel_id: channel } });
      toast.success(`${data.imported} lien(s) importé(s)`);
      loadLinks();
    } catch (e) { toast.error(apiError(e, 'Import échoué')); }
  };

  const createLink = async () => {
    if (!title.trim()) { toast.error('Donne un nom au lien (ex : bio, story du 12…)'); return; }
    setCreating(true);
    try {
      const { data } = await api.post('/tracking/links', { account_id: account, channel_id: channel, source, title: title.trim() });
      toast.success('Lien créé');
      setTitle('');
      // #146 : le nouveau lien s'affiche TOUT DE SUITE (0 rejoint, forcément), sans recharger les stats de tous les liens.
      if (data?.id && data?.link) {
        const ligne = { id: data.id, title: data.title, source: data.source || 'Autre', link: data.link, joins: 0, joins_total: 0,
          joins_period: 0, joins_depuis: null, joins_est_cumule: false, new_convs: 0, dm_rate: 0, dm_rate_period: 0, sales: 0, revenue: 0, ltv: 0 };
        creesIci.current.push({ canal: `${account}:${channel}`, ligne }); // gardée si des stats parties avant arrivent après
        setLinks(prev => [ligne, ...prev.filter(x => x.id !== ligne.id && x.link !== ligne.link)]);
        setNouveauId(ligne.id);
        oublierCanal(account, channel);
      } else {
        loadLinks();
      }
    } catch (e) { toast.error(apiError(e, 'Création échouée')); }
    finally { setCreating(false); }
  };

  const deleteLink = async (l) => {
    if (!window.confirm(`Supprimer le lien « ${l.source ? l.source + ' · ' : ''}${l.title} » ? Il sera révoqué sur Telegram et ne fonctionnera plus.`)) return;
    setDeletingId(l.id);
    try {
      await api.delete(`/tracking/links/${l.id}`);
      toast.success('Lien supprimé');
      setLinks(prev => prev.filter(x => x.id !== l.id));
      creesIci.current = creesIci.current.filter(c => c.ligne.id !== l.id);
      oublierCanal(account, channel);
    } catch (e) { toast.error(apiError(e, 'Suppression échouée')); }
    finally { setDeletingId(''); }
  };

  const copy = (l) => { navigator.clipboard?.writeText(l); toast.success('Lien copié'); };

  const resolveChannel = async () => {
    if (!resolveQuery.trim() || !account) return;
    setResolving(true);
    try {
      const { data } = await api.post('/tracking/channels/resolve', { account_id: account, query: resolveQuery.trim() });
      toast.success(`Canal ajouté : ${data.title}`);
      setResolveQuery('');
      setChannels(prev => (prev.some(c => c.id === data.id) ? prev : [...prev, data]));
      setChannel(data.id);
      setChannelOf(account);
    } catch (e) { toast.error(apiError(e, 'Canal introuvable')); }
    finally { setResolving(false); }
  };

  const renameLink = async (l) => {
    const name = window.prompt('Nouveau nom de la source :', l.title || '');
    if (name == null || !name.trim() || name.trim() === l.title) return;
    try {
      await api.patch(`/tracking/links/${l.id}`, { title: name.trim() });
      toast.success('Source renommée');
      // Le nom change, pas les chiffres : mise à jour sur place (avant : rechargement complet des stats).
      setLinks(prev => prev.map(x => (x.id === l.id ? { ...x, title: name.trim() } : x)));
      creesIci.current = creesIci.current.map(c => (c.ligne.id === l.id ? { ...c, ligne: { ...c.ligne, title: name.trim() } } : c));
      oublierCanal(account, channel);
    } catch (e) { toast.error(apiError(e, 'Renommage échoué')); }
  };

  const linkSources = Array.from(new Set(links.map(l => l.source).filter(Boolean))).sort();
  const filteredLinks = filterSource ? links.filter(l => l.source === filterSource) : links;
  const visibleLinks = useMemo(() => {
    const num = k => (l) => Number(l[k]) || 0;
    const str = k => (l) => String(l[k] || '').toLowerCase();
    const getters = { source: str('source'), title: str('title'),
      joins: (l) => rejoints(l),
      new_convs: num('new_convs'),
      dm_rate: (l) => Number(l.dm_rate_period ?? l.dm_rate) || 0,
      sales: num('sales'), revenue: num('revenue'), ltv: num('ltv') };
    const g = getters[sortKey] || num('joins');
    const arr = [...filteredLinks].sort((a, b) => {
      // Le lien tout juste créé reste en tête (sinon, avec 0 rejoint, il partait en bas du tableau).
      if (nouveauId && (a.id === nouveauId) !== (b.id === nouveauId)) return a.id === nouveauId ? -1 : 1;
      const va = g(a), vb = g(b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filteredLinks, sortKey, sortDir, nouveauId]);
  // Liste affichée avant ses chiffres (#146) : une ligne sans `joins` attend encore ses stats → « … ».
  const aStats = (l) => l.joins !== undefined;
  const statsPretes = links.every(aStats);
  const totalJoins = links.reduce((s, l) => s + rejoints(l), 0);
  const debutHisto = jourFr(links.map(depuis).filter(d => d && d !== 'toujours').sort()[0]);
  const totalConvs = links.reduce((s, l) => s + (l.new_convs || 0), 0);
  const totalSales = links.reduce((s, l) => s + (l.sales || 0), 0);
  const totalRevenue = links.reduce((s, l) => s + (l.revenue || 0), 0);
  const avgDM = totalJoins ? ((totalConvs / totalJoins) * 100).toFixed(1) : '0';
  // Top source = celle qui ramène le PLUS de nouvelles conversations (sur la période).
  const convBySource = {};
  links.forEach(l => { const s = l.source || 'Autre'; convBySource[s] = (convBySource[s] || 0) + (l.new_convs || 0); });
  const topSource = Object.entries(convBySource).sort((a, b) => b[1] - a[1])[0];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <PageHeader
        title="Tracking links"
        subtitle="Liens d'invitation nommés par source — joins, taux de DM, ventes et LTV par lien"
        onRefresh={() => { loadChannels(true); loadLinks(true); }}
        actions={<><Button onClick={() => setShowForm(s => !s)} className="bg-indigo-500 hover:bg-indigo-600 h-9"><Plus size={14} className="mr-1.5" /> {showForm ? 'Fermer' : 'Créer un lien'}</Button><AccountSelector accounts={accounts} value={account} onChange={setAccount} /></>}
      />

      <div className="flex gap-1 p-1 rounded-lg bg-white/5 border border-white/10 w-fit mb-2">
        {PERIODS.map(([k, label]) => (
          <button key={k} onClick={() => setPeriod(k)}
            className={`px-3 h-7 rounded-md text-xs font-medium transition-colors ${period === k ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white'}`}>{label}</button>
        ))}
      </div>
      <p className="text-[11px] text-slate-500 mb-4">
        Tous les chiffres sont sur la <b className="text-slate-400">période choisie</b> ci-dessus.
        « Rejoints » = les personnes qui ont <b className="text-slate-400">rejoint le Telegram</b> par ce lien —
        celles reparties depuis restent comptées, elles ont bien été apportées par la page.
        {debutHisto && <> L'historique des compteurs démarre le <b className="text-slate-400">{debutHisto}</b> :
        au-delà, les chiffres partent de cette date (indiquée au survol).</>}
      </p>

      {funnel.length > 0 && (
        <div className="mb-5">
          <h3 className="text-sm font-semibold text-white mb-2">🎯 Combien de rejoints pour une vente ? — par modèle</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {funnel.map(f => (
              <div key={f.account_id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold text-white">{f.modele}</span>
                  <span className="text-xs text-emerald-400 font-bold">{(f.ca || 0).toLocaleString('fr-FR')} €</span>
                </div>
                <div className="flex items-center gap-1 text-[11px] text-slate-300 flex-wrap">
                  <span className="px-2 py-1 rounded-lg bg-white/[0.05]"
                    title={f.rejoints_cumules ? "Depuis le début : l'historique ne permet pas encore de compter cette période à part." : 'Sur la période choisie.'}><b className="text-white">{f.rejoints}</b> rejoints</span>
                  <span className="text-slate-600">→</span>
                  <span className="px-2 py-1 rounded-lg bg-white/[0.05]"><b className="text-white">{f.ont_ecrit}</b> ont écrit{f.taux_ecrit ? ` (${f.taux_ecrit}%)` : ''}</span>
                  <span className="text-slate-600">→</span>
                  <span className="px-2 py-1 rounded-lg bg-white/[0.05]"><b className="text-white">{f.recu_lien}</b> ont reçu un lien</span>
                  <span className="text-slate-600">→</span>
                  <span className="px-2 py-1 rounded-lg bg-emerald-500/10 text-emerald-300"><b>{f.payes}</b> ont payé</span>
                </div>
                <div className="mt-2 text-[12px]">
                  {f.rejoints_par_vente
                    ? <span className="text-indigo-300 font-semibold">≈ {f.rejoints_par_vente} rejoints = 1 vente</span>
                    : <span className="text-slate-500">pas encore de vente sur la période</span>}
                  {f.panier > 0 && <span className="text-slate-400"> · panier {f.panier.toLocaleString('fr-FR')} €</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showForm && (<>
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-4 mb-5">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500">Canal Telegram</label>
            <select value={channel} onChange={e => setChannel(e.target.value)}
              className="block mt-1 bg-[#18171C] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none min-w-[240px]">
              {channels.length === 0 && <option value="">{scanning ? 'Recherche des canaux…' : 'Aucun canal — ajoute-le par @nom / lien ci-dessous'}</option>}
              {channels.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </div>
          <Button variant="outline" onClick={importLinks} disabled={!channel}
            className="border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 h-9">
            <RefreshCw size={14} className="mr-1.5" /> Importer les liens existants
          </Button>
          {scanning && (
            <span className="inline-flex items-center gap-1.5 text-[12px] text-slate-400 pb-2">
              <Loader2 size={13} className="animate-spin text-sky-400" /> Scan des canaux en cours (en arrière-plan)…
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[220px]">
            <label className="text-[11px] uppercase tracking-wider text-slate-500">Ajouter un canal directement</label>
            <input value={resolveQuery} onChange={e => setResolveQuery(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') resolveChannel(); }}
              placeholder="@nomducanal ou https://t.me/…"
              className="block w-full mt-1 bg-[#18171C] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none placeholder:text-slate-600" />
          </div>
          <Button variant="outline" onClick={resolveChannel} disabled={!resolveQuery.trim() || resolving}
            className="border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 h-9">
            {resolving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Plus size={14} className="mr-1.5" />} Ajouter
          </Button>
        </div>
        <div className="flex flex-wrap items-end gap-2 pt-3 border-t border-white/5">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500">Source</label>
            <select value={source} onChange={e => setSource(e.target.value)}
              className="block mt-1 bg-[#18171C] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none min-w-[150px]">
              {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-[11px] uppercase tracking-wider text-slate-500">Nom du lien</label>
            <input value={title} onChange={e => setTitle(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') createLink(); }}
              placeholder="bio, story du 12, lien promo…"
              className="block w-full mt-1 bg-[#18171C] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none placeholder:text-slate-600" />
          </div>
          <Button onClick={createLink} disabled={!channel || creating} className="bg-indigo-500 hover:bg-indigo-600 h-9">
            {creating ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Plus size={14} className="mr-1.5" />} Créer le lien
          </Button>
        </div>
      </div>

      <div className="flex items-start gap-2 text-[12px] text-slate-400 rounded-xl border border-sky-500/15 bg-sky-500/[0.04] px-4 py-3 mb-5">
        <Info size={15} className="text-sky-400 shrink-0 mt-0.5" />
        <span>Le compte doit être <span className="text-white">administrateur du canal</span> (avec le droit « Inviter via un lien ») pour créer et suivre ses liens. Seuls les <span className="text-white">canaux</span> sont affichés (les groupes et le stockage interne sont masqués).</span>
      </div>
      </>)}

      {links.length > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
            {[
              ['Rejoints', totalJoins.toLocaleString('fr-FR'), 'Personnes ayant rejoint le Telegram par ces liens, sur la période choisie.'],
              ['Depuis le début', links.reduce((s, l) => s + (Number(l.joins_total ?? l.joins) || 0), 0).toLocaleString('fr-FR'), 'Tous ceux qui ont rejoint depuis le début — ne redescend JAMAIS, même si des membres partent ou qu\'un lien est supprimé'],
              ['Nouvelles convs', totalConvs.toLocaleString('fr-FR')],
              ['Taux DM', avgDM + ' %'],
              ['Ventes', totalSales.toLocaleString('fr-FR')],
              ['Revenu', totalRevenue.toLocaleString('fr-FR') + ' €'],
            ].map(k => (
              <div key={k[0]} title={k[2] || ''} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                <div className="text-[11px] uppercase tracking-wider text-slate-500">{k[0]}</div>
                <div className="text-2xl font-bold text-white mt-1" data-testid={`kpi-${k[0]}`}>{statsPretes ? k[1] : EN_ATTENTE}</div>
              </div>
            ))}
          </div>
          {topSource && topSource[1] > 0 && (
            <div className="rounded-2xl border border-indigo-500/20 bg-indigo-500/[0.06] px-5 py-3 mb-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
              <span className="text-lg">🏆</span>
              <span className="text-slate-300">Source qui ramène le plus de conversations :</span>
              <span className="font-bold text-white">{topSource[0]}</span>
              <span className="text-indigo-300 font-semibold">· {topSource[1]} nouvelles convs</span>
            </div>
          )}
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-5">
            <h3 className="font-semibold text-white">Nouvelles conversations par jour</h3>
            <div className="text-xs text-slate-500 mb-2">tous les liens · {(PERIODS.find(p => p[0] === period) || [])[1]} · survole la courbe pour le détail</div>
            <ConvAreaChart data={timeseries} unit="nouvelles convs" emptyText="Aucune nouvelle conversation via tes liens sur cette période." />
          </div>
        </>
      )}

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between gap-3">
          <span className="text-sm font-semibold text-white">{visibleLinks.length} lien(s){filterSource ? ` · ${filterSource}` : ''}<span className="text-[11px] text-slate-500 font-normal ml-2">· clique une colonne (Joins, Ventes…) pour trier</span></span>
          <div className="flex items-center gap-2">
            <select value={filterSource} onChange={e => setFilterSource(e.target.value)}
              className="bg-[#18171C] border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-slate-200 outline-none">
              <option value="">Toutes les sources</option>
              {linkSources.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {loadingLinks && <Loader2 size={14} className="animate-spin text-slate-400" />}
          </div>
        </div>
      {detail && (
        <div className="fixed inset-0 z-[70] flex items-end md:items-center justify-center bg-black/60 p-0 md:p-6"
          onClick={() => setDetail(null)}>
          <div className="w-full md:max-w-2xl max-h-[85vh] overflow-y-auto rounded-t-2xl md:rounded-2xl border border-white/10 bg-[#0c0d14] p-5"
            onClick={(e) => e.stopPropagation()} data-testid="panneau-fans">
            <div className="flex items-start gap-3 mb-1">
              <div className="flex-1">
                <div className="text-base font-bold text-white">{detail.lien}</div>
                <div className="text-[11px] text-slate-400">{detail.source} · période affichée : {detail.periode}</div>
              </div>
              <button onClick={() => setDetail(null)} className="text-slate-500 hover:text-white"><X size={18} /></button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 my-4">
              {[['Ont rejoint en tout', detail.entres_par_ce_lien_tout_temps],
                ['Ont écrit', detail.conversations_ouvertes],
                ['Ont payé', detail.fans_payeurs],
                ['CA période', (detail.ca_periode || 0).toFixed(2) + ' €']].map(([k, v]) => (
                <div key={k} className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">{k}</div>
                  <div className="text-lg font-bold text-white">{v}</div>
                </div>
              ))}
            </div>

            {(detail.fans || []).length === 0 ? (
              <div className="text-sm text-slate-400 py-6 text-center">
                {detail.note || 'Personne n\'a payé sur cette période.'}
              </div>
            ) : (
              <div className="space-y-2">
                {detail.fans.map((f) => (
                  <div key={f.conversation_id} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-white">{f.fan}</span>
                      {f.arrive_sur_la_periode
                        ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300">arrivé sur la période</span>
                        : <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.06] text-slate-400">déjà là (arrivé le {f.arrive_le})</span>}
                      <span className="ml-auto text-emerald-300 font-bold">{f.paye_periode.toFixed(2)} €</span>
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1">
                      {f.ventes.map((v, i) => (
                        <span key={i}>{i > 0 && ' · '}{v.montant} € le {v.quand} — {v.chatteur}</span>
                      ))}
                      {f.total_depense_tout_temps > f.paye_periode && (
                        <span className="text-slate-500"> · {f.total_depense_tout_temps} € depuis le début</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <p className="text-[11px] text-slate-500 mt-4 leading-relaxed">
              À lire avec précaution : le CA d'une période compte les paiements faits par
              <b className="text-slate-400"> tous les fans jamais arrivés par ce lien</b>, pas seulement
              ceux de la période. Un fan venu il y a trois mois qui paie aujourd'hui compte ici —
              c'est pour ça qu'un lien peut afficher du revenu sans aucun nouveau rejoint.
            </p>
          </div>
        </div>
      )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-500 text-left border-b border-white/5">
                {[['source', 'Source', 'left', 'px-5'], ['title', 'Nom', 'left', 'px-3'], [null, 'Lien', 'left', 'px-3'],
                  ['joins', 'Rejoints', 'right', 'px-3'], ['new_convs', 'Nouvelles convs', 'right', 'px-3'],
                  ['dm_rate', 'Taux DM', 'right', 'px-3'], ['sales', 'Ventes', 'right', 'px-3'],
                  ['revenue', 'Revenu', 'right', 'px-3'], ['ltv', 'LTV', 'right', 'px-3']].map(([key, label, align, pad]) => (
                  <th key={label} title="Sur la période choisie"
                    className={`${pad} py-2 font-semibold ${align === 'right' ? 'text-right' : ''} ${key ? 'cursor-pointer select-none hover:text-slate-300' : ''}`}
                    onClick={key ? () => toggleSort(key) : undefined} data-testid={key ? `sort-${key}` : undefined}>
                    {label}{key && (sortKey === key ? <span className="text-indigo-400">{sortDir === 'desc' ? ' ▼' : ' ▲'}</span> : <span className="text-slate-600"> ⇅</span>)}
                  </th>
                ))}
                <th className="px-3 py-2 font-semibold"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {visibleLinks.map(l => (
                <tr key={l.id} data-testid={`lien-${l.id}`} className={l.id === nouveauId ? 'bg-emerald-500/[0.07]' : 'hover:bg-white/[0.02]'}>
                  <td className="px-5 py-3">
                    <span className="inline-block px-2 py-0.5 rounded-md bg-white/[0.06] text-slate-200 text-xs font-medium">{l.source || 'Autre'}</span>
                  </td>
                  <td className="px-3 py-3 font-medium text-white">
                    <span className="inline-flex items-center gap-1.5 group">
                      {l.title}
                      <button onClick={() => renameLink(l)} title="Renommer" className="text-slate-500 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity">
                        <Pencil size={12} />
                      </button>
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <button onClick={() => copy(l.link)} title="Copier" className="text-sky-300 hover:underline text-xs truncate max-w-[150px] inline-block align-middle">
                      {String(l.link).replace('https://', '')}
                    </button>
                  </td>
                  <td className="px-3 py-3 text-right text-white"
                    title={depuis(l)
                      ? (jourFr(depuis(l))
                        ? `Compté depuis le ${jourFr(depuis(l))} : l'historique des compteurs ne remonte pas plus loin.`
                        : "Aucun historique pour ce lien : total depuis le début.")
                      : 'Ont rejoint sur la période choisie.'}>
                    {aStats(l) ? rejoints(l) : EN_ATTENTE}
                  </td>
                  <td className="px-3 py-3 text-right text-slate-300">{aStats(l) ? l.new_convs : EN_ATTENTE}</td>
                  <td className="px-3 py-3 text-right text-indigo-300">{aStats(l) ? `${l.dm_rate_period ?? l.dm_rate}%` : EN_ATTENTE}</td>
                  <td className="px-3 py-3 text-right text-slate-300">{aStats(l) ? l.sales : EN_ATTENTE}</td>
                  <td className="px-3 py-3 text-right text-emerald-300 font-medium">{aStats(l) ? `${Number(l.revenue).toFixed(2)} €` : EN_ATTENTE}</td>
                  <td className="px-3 py-3 text-right text-amber-300">{aStats(l) ? `${Number(l.ltv).toFixed(2)} €` : EN_ATTENTE}</td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <button onClick={() => voirFans(l, period)} disabled={detailLoading === l.id}
                      title="Voir qui est arrivé par ce lien et qui a payé" data-testid={`fans-${l.id}`}
                      className="text-slate-500 hover:text-indigo-300 transition-colors mr-3 disabled:opacity-40">
                      {detailLoading === l.id ? <Loader2 size={14} className="animate-spin" /> : <Users size={14} />}
                    </button>
                    <button onClick={() => deleteLink(l)} disabled={deletingId === l.id} title="Supprimer le lien"
                      className="text-slate-500 hover:text-red-400 transition-colors disabled:opacity-40">
                      {deletingId === l.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                    </button>
                  </td>
                </tr>
              ))}
              {!loadingLinks && visibleLinks.length === 0 && (
                <tr><td colSpan={10} className="px-5 py-12 text-center text-slate-500 text-sm">
                  {links.length > 0
                    ? `Aucun lien pour « ${filterSource} ».`
                    : 'Aucun lien suivi. Choisis un canal, puis « Importer les liens existants » ou crée un lien ci-dessus.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
